import { Pipe, PipeTransform } from '@angular/core';
import * as moment from 'moment';
import { Store } from '@ngrx/store';
import { MatchBoardState } from '../store/state/reducers/matchboard.reducers';
import { getCurrencyPairsSelector } from '../store/state/selectors/matchboard.selectors';
import { CurrencyDto } from '../models/CurrencyDto';

@Pipe({ name: 'formatSixLetter' })
export class SixLetter implements PipeTransform {
  transform(e: string) {
    return e.substring(0, 3) + '/' + e.substring(3, 6);
  }
}

@Pipe({ name: 'getFirstCC' })
export class FirstCC implements PipeTransform {
  transform(e: string) {
    return e.substring(0, 3);
  }
}

@Pipe({ name: 'getSecondCC' })
export class SecondCC implements PipeTransform {
  transform(e: string) {
    return e.substring(3, 6);
  }
}

@Pipe({ name: 'shortNumber' })
export class ShortNumberPipe implements PipeTransform {
  transform(number: number, args?: any): any {
    if (isNaN(number)) return null; // will only work value is a number
    if (number === null) return null;
    if (number === 0) return null;
    if (number <= 1000) return number;
    let abs = Math.abs(number);
    const rounder = Math.pow(10, 1);
    const isNegative = number < 0; // will also work for Negetive numbers
    let key = '';

    const powers = [
      {key: 'Q', value: Math.pow(10, 15)},
      {key: 'T', value: Math.pow(10, 12)},
      {key: 'B', value: Math.pow(10, 9)},
      {key: 'M', value: Math.pow(10, 6)},
      {key: 'k', value: 1000}
    ];

    for (let i = 0; i < powers.length; i++) {
      let reduced = abs / powers[i].value;
      reduced = Math.round(reduced * rounder) / rounder;
      if (reduced >= 1) {
        abs = reduced;
        key = powers[i].key;
        break;
      }
    }

    return (isNegative ? '-' : '') + abs + key;
  }
}
@Pipe({ name: 'shortNumberFormatByCurrencyPair' })
export class ShortNumberFormatByCurrencyPairPipe implements PipeTransform {

  currencyPairs: CurrencyDto[];

  constructor(private readonly store: Store<MatchBoardState>) {
  }

  transform(number: number, sixLetterCode: string): any {
    if (isNaN(number)) return null; // will only work value is a number
    if (number === null) return null;
    if (number === 0) return null;
    this.store.select(getCurrencyPairsSelector).subscribe((currencyPairs) => {
      this.currencyPairs = currencyPairs;
    });
    if (number <= 1000) {
      const curr = this.currencyPairs.find(c => c.sixLetterCode === sixLetterCode);
      if (!curr) {
        return Number(number.toFixed(1));
      }
      return Number(number.toFixed(curr.spotPricePrecision));
    }
    let abs = Math.abs(number);
    const rounder = Math.pow(10, 1);
    const isNegative = number < 0; // will also work for Negetive numbers
    let key = '';

    const powers = [
      {key: 'Q', value: Math.pow(10, 15)},
      {key: 'T', value: Math.pow(10, 12)},
      {key: 'B', value: Math.pow(10, 9)},
      {key: 'M', value: Math.pow(10, 6)},
      {key: 'k', value: 1000}
    ];

    for (let i = 0; i < powers.length; i++) {
      let reduced = abs / powers[i].value;
      reduced = Math.round(reduced * rounder) / rounder;
      if (reduced >= 1) {
        abs = reduced;
        key = powers[i].key;
        break;
      }
    }

    const cp = this.currencyPairs.find(c => c.sixLetterCode === sixLetterCode);
    if (!cp) {
      return (isNegative ? '-' : '') + abs + key;
    }

    abs = Number(abs.toFixed(1));
    return (isNegative ? '-' : '') + abs + key;
  }
}


@Pipe({ name: 'getCurrencySymbol' })
export class CurrencySymbolPipe implements PipeTransform {
  transform(string: string, args?: any): any {
    if (string === null) return null;
    if (string === 'EUR') return '€';
    if (string === 'USD') return '$';
    if (string === 'JPY') return '¥';
    if (string === 'PLN') return 'zł';
    if (string === 'GBP') return '£';
    if (string === 'BTC') return '₿';
    if (string === 'GUS') return 'GUS'; // GuessChain
    if (string === 'HUS') return 'HUS'; // HUSSY
    if (string === 'PAX') return 'PAX'; // Paxos Standard
    if (string === 'TUS') return 'TUS';
    if (string === 'UST') return 'UST';
    if (string === 'USC') return 'USC';
    if (string === 'XRP') return 'XRP';
  }
}

@Pipe({ name: 'dateRangeFilter' })
export class DateRangeFilter implements PipeTransform {

  transform(items: any[], beginDate: Date, endDate: Date, dateProp: string = 'timestamp'): any {
    return items.filter(item => {
      return new Date(item[dateProp]) >= new Date(beginDate.getFullYear(), beginDate.getMonth(), beginDate.getDate(), 0, 0, 0) &&
        new Date(item[dateProp]) <= new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);
    });
  }

}


@Pipe({ name: 'priceFactorPipe' })
export class PriceFactorPipe implements PipeTransform {

  currencyPairs: CurrencyDto[];

  constructor(private readonly store: Store<MatchBoardState>) {
  }

  transform(price: number, sixLetterCode: string, reverse: boolean = false): any {
    this.store.select(getCurrencyPairsSelector).subscribe((currencyPairs) => {
      this.currencyPairs = currencyPairs;
    });
    const curr = this.currencyPairs.find(c => c.sixLetterCode === sixLetterCode);
    if (!curr) {
      return price;
    }

    if (reverse) {
      return price / curr.swapPriceFactor;
    }

    return Number((curr.swapPriceFactor * price).toFixed(curr.swapPricePrecision));
  }
}

@Pipe({ name: 'deletePointIfCpIsJpy' })
export class DeletePointIfCpIsJpy implements PipeTransform {

  transform(price: string, sixLetterCode: string): any {
    if (sixLetterCode.includes('JPY')) {
      return price.replace('.', '');
    }
    return price;
  }
}

@Pipe({name: 'formatDateToNYTime'})
export class FormatDateToNYTime implements PipeTransform {
  /**
   * @param value date in string to be formatted
   * @param timeOff
   * @param pattern default YYYY.MM.DD (2017.12.31)
   */
  transform(value: string, timeOff: number, pattern: string = 'YYYY.MM.DD'): any {
    const dateString: string = moment(value)
      .utcOffset(timeOff, false)
      .format(pattern);

    return dateString === 'Invalid date' ? '' : dateString;
  }
}

// @Pipe({ name: 'orderFilterBySwap' })
// export class OrderFilterBySwap implements PipeTransform {
//
//   transform(items: Order[], swap: string): any {
//     console.log(items);
//     return items.filter(i => i.swap === swap);
//   }
//
// }
